import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, filter, of, switchMap, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  DeleteDialogData
} from '../../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { FetchOptionsStateModel } from '../../../core/state/fetchOptions.state';
import { ExplanationActions } from './explanation.actions';
import { ExplanationJsonModel } from '../../explanation.json.model';
import { ExplanationService } from '../../services/explanation.service';
import { EditExplanationComponent } from '../../edit-explanation/edit-explanation/edit-explanation.component';

export interface ExplanationStateModel {
  explanations: ExplanationJsonModel[]
  explanation: ExplanationJsonModel | null
}

@State<ExplanationStateModel>({
  name: 'explanation',
  defaults: {
    explanations: [],
    explanation: null
  }
})
@Injectable()
export class ExplanationState {
  private readonly explanationService = inject(ExplanationService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);

  @Selector()
  static getState(state: ExplanationStateModel) {
    return state;
  }

  @Selector()
  static getExplanations(state: ExplanationStateModel) {
    return state.explanations;
  }

  @Selector()
  static getExplanation(state: ExplanationStateModel) {
    return state.explanation;
  }

  @Selector()
  static getAmountOfExplanations(state: ExplanationStateModel) {
    return state.explanations?.length ?? 0;
  }

  @Selector()
  static explanationsIsEmpty(state: ExplanationStateModel) {
    return (state.explanations?.length ?? 0) <= 0;
  }

  @Action(ExplanationActions.Fetch)
  onFetchExplanations(ctx: StateContext<ExplanationStateModel>, action: ExplanationActions.Fetch) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides
    };

    return this.explanationService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new ExplanationActions.FetchSuccess(res[0]));
      })
    );
  }

  @Action(ExplanationActions.FetchSuccess)
  onFetchExplanationsSuccess(ctx: StateContext<ExplanationStateModel>, { payload }: ExplanationActions.FetchSuccess) {
    ctx.setState({ ...ctx.getState(), explanations: payload });
  }

  @Action(ExplanationActions.FetchOne)
  onFetchOneExplanation(ctx: StateContext<ExplanationStateModel>, action: ExplanationActions.FetchOne) {
    return this.explanationService.getOneWithId(action.id, {}).pipe(
      tap((res: ExplanationJsonModel) => {
        ctx.dispatch(new ExplanationActions.FetchOneSuccess(res));
      })
    );
  }

  @Action(ExplanationActions.FetchOneSuccess)
  onFetchOneSuccess(ctx: StateContext<ExplanationStateModel>, { payload }: ExplanationActions.FetchOneSuccess) {
    ctx.setState({ ...ctx.getState(), explanation: payload });
  }

  @Action(ExplanationActions.FetchAppend)
  onFetchAppendExplanations(ctx: StateContext<ExplanationStateModel>, action: ExplanationActions.FetchAppend) {
    const localFetchOptions = this.store.selectSnapshot<FetchOptionsStateModel>(state => state.fetchOptions);

    const { skipStoreDispatch, ...restOverrides } = action.overrideOptions || {};

    const updatedFetchOptions = {
      ...localFetchOptions,
      ...restOverrides,
    };

    return this.explanationService.getAllWithPagination({}, {
      ...updatedFetchOptions,
      skipStoreDispatch
    }).pipe(
      tap(res => {
        ctx.dispatch(new ExplanationActions.FetchSuccessAppend(res[0]));
      })
    );
  }

  @Action(ExplanationActions.FetchSuccessAppend)
  onFetchExplanationsSuccessAppend(
    ctx: StateContext<ExplanationStateModel>,
    { payload }: ExplanationActions.FetchSuccess
  ) {
    const state = ctx.getState();
    ctx.setState({
      ...ctx.getState(),
      explanations: [...state.explanations, ...payload]
    });
  }

  @Action(ExplanationActions.Add)
  onAdd(ctx: StateContext<ExplanationStateModel>, { payload }: ExplanationActions.Add) {
    return this.dialog.open<EditExplanationComponent, ExplanationJsonModel, ExplanationJsonModel>(EditExplanationComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((explanation): explanation is ExplanationJsonModel => !!explanation),
        switchMap(explanation => this.explanationService.createAndSave(explanation)),
        catchError(() => of()),
        tap(() => ctx.dispatch(new ExplanationActions.Fetch()))
      );
  }

  @Action(ExplanationActions.Edit)
  onEdit(ctx: StateContext<ExplanationStateModel>, { payload }: ExplanationActions.Edit) {
    return this.dialog.open<EditExplanationComponent, ExplanationJsonModel, ExplanationJsonModel>(EditExplanationComponent, { data: payload })
      .afterClosed()
      .pipe(
        filter((explanation): explanation is ExplanationJsonModel => !!explanation),
        switchMap(explanation => this.explanationService.update(explanation)),
        // currently ignores errors
        catchError(() => of()),
        tap(() => ctx.dispatch(new ExplanationActions.Fetch()))
      );
  }

  @Action(ExplanationActions.Delete)
  onDelete(ctx: StateContext<ExplanationJsonModel>, { payload }: ExplanationActions.Delete) {
    return this.dialog.open<ConfirmDialogComponent, DeleteDialogData, boolean>(ConfirmDialogComponent, {
      data: {
        id: payload.id,
        name: payload.name,
        itemType: 'Erkläreinheit',
        deleteFn: () => {
          return payload.destroy();
        }
      }
    })
      .afterClosed()
      .pipe(
        tap(success => {
          if (!success) return;
          ctx.dispatch(new ExplanationActions.Fetch());
        })
      );
  }
}
